import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Loader2, BookOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@kahani.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-rose-600/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-600 to-violet-600 flex items-center justify-center glow-primary">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-2xl text-white">Kahani<span className="text-rose-500">.</span></span>
        </div>

        <div className="bg-[#12141D] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-rose-500" />
            <h1 className="font-display font-bold text-xl text-white">Admin Portal</h1>
          </div>
          <p className="text-sm text-gray-400 mb-7">Sign in to manage series, chapters and panels.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-gray-300 text-sm">Email</Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Password</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500"
              />
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold h-11 glow-primary"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-gray-600 mt-5 font-mono-kh">
          Demo: admin@kahani.com / kahani123
        </p>
      </div>
    </div>
  );
}
